import { Component, OnInit } from '@angular/core';
import { BY_COMMODITY, CropDefault } from './crop-type-match-catalog';

type RowIssue = 'ok' | 'missing' | 'duplicate';

interface CommodityRow {
	name: string;
	valid: boolean;
	issueCount: number;
}

interface CropRow {
	crop: CropDefault;
	issue: RowIssue;
	summary: string;
}

const ATTR_KEYS: (keyof CropDefault)[] = [
	'genericName', 'plantingSystem', 'brand', 'region', 'season',
	'productClass', 'age', 'bedWidth', 'rows'
];

@Component({
	selector: 'crop-type-admin-proto',
	template: `
		<div class="admin">
			<h2>Crop type attribute admin (proto)</h2>

			<div *ngIf="!selected">
				<label class="filter">
					<input type="checkbox" [ngModel]="invalidOnly" (ngModelChange)="setInvalidOnly($event)" />
					Invalid only
				</label>
				<table>
					<thead><tr><th>Commodity</th><th>Status</th><th>Issues</th></tr></thead>
					<tbody>
						<tr *ngFor="let c of commodities" (click)="open(c.name)" class="click">
							<td>{{ c.name }}</td>
							<td [class.bad]="!c.valid">{{ c.valid ? 'Valid' : 'Invalid' }}</td>
							<td>{{ c.issueCount || '—' }}</td>
						</tr>
					</tbody>
				</table>
			</div>

			<div *ngIf="selected">
				<button type="button" (click)="back()">← Commodities</button>
				<h3>{{ selected }}</h3>
				<table>
					<thead><tr><th>Crop type</th><th>Status</th><th>Attributes</th><th></th></tr></thead>
					<tbody>
						<ng-container *ngFor="let r of rows; trackBy: trackRow">
							<tr [class.bad]="r.issue !== 'ok'">
								<td>{{ r.crop.name }}</td>
								<td>{{ issueLabel(r.issue) }}</td>
								<td class="attrs">{{ r.summary }}</td>
								<td><button type="button" (click)="startEdit(r.crop.name)">Edit</button></td>
							</tr>
							<tr *ngIf="editingName === r.crop.name" class="edit">
								<td colspan="4">
									<label *ngFor="let k of attrKeys">
										{{ k }}
										<input
											[type]="k === 'bedWidth' || k === 'rows' ? 'number' : 'text'"
											[ngModel]="displayAttr(r.crop, k)"
											(ngModelChange)="setAttr(r.crop, k, $event)"
										/>
									</label>
									<button type="button" (click)="stopEdit()">Done</button>
								</td>
							</tr>
						</ng-container>
					</tbody>
				</table>
			</div>
		</div>
	`,
	styles: [`
		.admin { max-width: 900px; margin: 2rem auto; padding: 1rem; font-family: sans-serif; }
		table { width: 100%; border-collapse: collapse; margin-top: 0.75rem; }
		th, td { text-align: left; padding: 0.4rem 0.5rem; border-bottom: 1px solid #ddd; vertical-align: top; }
		.click { cursor: pointer; }
		.click:hover { background: #f5f5f5; }
		.bad { color: #a00; font-weight: 600; }
		.attrs { font-size: 0.85rem; color: #444; }
		.filter { display: block; margin: 0.5rem 0; }
		.edit label { display: inline-block; width: 30%; margin: 0.35rem 1% 0.35rem 0; font-size: 0.85rem; }
		.edit input { display: block; width: 100%; margin-top: 0.15rem; box-sizing: border-box; }
		button { cursor: pointer; }
	`]
})
export class CropTypeAdminProtoComponent implements OnInit {
	attrKeys = ATTR_KEYS;
	selected: string | null = null;
	editingName: string | null = null;
	invalidOnly = false;
	commodities: CommodityRow[] = [];
	rows: CropRow[] = [];

	ngOnInit(): void {
		this.refreshCommodities();
	}

	trackRow(_i: number, r: CropRow): string {
		return r.crop.name;
	}

	setInvalidOnly(v: boolean): void {
		this.invalidOnly = v;
		this.refreshCommodities();
	}

	open(name: string): void {
		this.selected = name;
		this.editingName = null;
		this.refreshRows();
	}

	back(): void {
		this.selected = null;
		this.editingName = null;
		this.refreshCommodities();
	}

	startEdit(name: string): void {
		this.editingName = name;
	}

	stopEdit(): void {
		this.editingName = null;
		this.refreshRows();
	}

	issueLabel(issue: RowIssue): string {
		if (issue === 'missing') {
			return 'No attributes';
		}

		if (issue === 'duplicate') {
			return 'Duplicate attributes';
		}

		return 'OK';
	}

	displayAttr(crop: CropDefault, key: keyof CropDefault): string {
		let v = crop[key];
		return v == null ? '' : String(v);
	}

	setAttr(crop: CropDefault, key: keyof CropDefault, raw: any): void {
		let next: any;

		if (key === 'bedWidth' || key === 'rows') {
			next = raw === '' || raw == null ? null : Number(raw);
		} else {
			next = raw === '' || raw == null ? null : String(raw);
		}

		if (crop[key] === next) {
			return;
		}

		(crop as any)[key] = next;
		this.refreshRows();
	}

	refreshCommodities(): void {
		let names = Object.keys(BY_COMMODITY).sort();
		let rows = names.map(name => {
			let issues = buildRows(name).filter(r => r.issue !== 'ok').length;
			return { name, valid: issues === 0, issueCount: issues };
		});

		this.commodities = this.invalidOnly ? rows.filter(r => !r.valid) : rows;
	}

	refreshRows(): void {
		this.rows = this.selected ? buildRows(this.selected) : [];
	}
}

function buildRows(commodity: string): CropRow[] {
	let crops = BY_COMMODITY[commodity] || [];
	let counts: { [fp: string]: number } = {};

	for (let c of crops) {
		let fp = fingerprint(c);
		counts[fp] = (counts[fp] || 0) + 1;
	}

	return crops.map(crop => {
		let issue: RowIssue = 'ok';

		if (!crop.genericName) {
			issue = 'missing';
		} else if (counts[fingerprint(crop)] > 1) {
			issue = 'duplicate';
		}

		return { crop, issue, summary: attrSummary(crop) };
	});
}

function fingerprint(d: CropDefault): string {
	return ATTR_KEYS.map(k => {
		let v = d[k];
		return v == null ? '' : String(v);
	}).join('\0');
}

function attrSummary(d: CropDefault): string {
	return ATTR_KEYS
		.filter(k => d[k] != null && d[k] !== '')
		.map(k => k + '=' + d[k])
		.join(', ') || '(none)';
}
